/**
 * Hook for using the offline mutation queue
 *
 * Provides reactive access to the mutation queue state
 * and sync manager status for components.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  getMutationQueue,
  getPendingMutationCount,
  hasPendingMutations,
  onQueueUpdate,
  resetFailedMutations,
} from './mutationQueue';
import { syncManager } from './syncManager';
import { MutationQueue, SyncEvent, SyncProgress } from './types';

/**
 * Hook to access mutation queue state
 *
 * @example
 * ```tsx
 * const { pendingCount, isSyncing, startSync } = useMutationQueue();
 *
 * return (
 *   <div>
 *     {pendingCount > 0 && <Badge>{pendingCount} pending</Badge>}
 *     {isSyncing && <Spinner />}
 *   </div>
 * );
 * ```
 */
export function useMutationQueue() {
  const [queue, setQueue] = useState<MutationQueue>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [lastSyncEvent, setLastSyncEvent] = useState<SyncEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load initial state
  useEffect(() => {
    const loadInitialState = async () => {
      try {
        const [queueData, count] = await Promise.all([getMutationQueue(), getPendingMutationCount()]);
        setQueue(queueData);
        setPendingCount(count);
        setIsSyncing(syncManager.getIsSyncing());
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialState();
  }, []);

  // Subscribe to queue updates
  useEffect(() => {
    const unsubscribe = onQueueUpdate(async () => {
      const [queueData, count] = await Promise.all([getMutationQueue(), getPendingMutationCount()]);
      setQueue(queueData);
      setPendingCount(count);
    });

    return unsubscribe;
  }, []);

  // Subscribe to sync events
  useEffect(() => {
    const unsubscribe = syncManager.subscribe((event) => {
      setLastSyncEvent(event);

      switch (event.type) {
        case 'SYNC_STARTED':
          setIsSyncing(true);
          setSyncProgress({ total: event.payload.total, completed: 0, failed: 0 });
          break;

        case 'SYNC_PROGRESS':
          setSyncProgress(event.payload);
          break;

        case 'SYNC_COMPLETED':
          setIsSyncing(false);
          setSyncProgress(null);
          break;

        case 'SYNC_ERROR':
          // Event is informational, sync may continue
          break;

        default:
          // Handle other event types
          break;
      }
    });

    return unsubscribe;
  }, []);

  // Start sync manually
  const startSync = useCallback(() => {
    syncManager.startSync();
  }, []);

  // Retry failed mutations
  const retryFailed = useCallback(async () => {
    await resetFailedMutations();
    syncManager.startSync();
  }, []);

  // Check if there are any pending mutations
  const checkHasPending = useCallback(async () => {
    return hasPendingMutations();
  }, []);

  return {
    /** The full mutation queue */
    queue,
    /** Number of pending mutations */
    pendingCount,
    /** Whether sync is in progress */
    isSyncing,
    /** Current sync progress (null if not syncing) */
    syncProgress,
    /** Last sync event received */
    lastSyncEvent,
    /** Whether initial data is loading */
    isLoading,
    /** Manually trigger sync */
    startSync,
    /** Reset failed mutations and retry */
    retryFailed,
    /** Check if there are pending mutations */
    checkHasPending,
  };
}

/**
 * Simplified hook for just checking pending status
 * Use when you only need to show a badge/indicator
 */
export function usePendingMutationCount() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    // Load initial count
    getPendingMutationCount().then(setCount);

    // Subscribe to updates
    const unsubscribe = onQueueUpdate(async () => {
      const newCount = await getPendingMutationCount();
      setCount(newCount);
    });

    return unsubscribe;
  }, []);

  return count;
}
